import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  startAddHomeContentItem,
  startDeleteHomeContentItem,
  startLoadingHomeContentItems,
  startLoadingHomeContentTypes,
  startUpdateHomeContentItem,
} from '../../app/actions/homeContentActions';
import { resolveHomeLang } from '../../app/helpers/homeContentHelper';

const emptyDraft = (typeKey, lang) => ({
  type_key: typeKey,
  lang,
  sort: 1,
  title: '',
  text: '',
  image_url: '',
  reference: '',
  module_key: '',
  enabled: true,
});

const AdminHomeContentScreen = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { typesCatalog, items, loading } = useSelector((state) => state.homeContent);
  const [typeKey, setTypeKey] = useState('');
  const [lang, setLang] = useState(resolveHomeLang(i18n.language));
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft('', lang));

  useEffect(() => {
    dispatch(startLoadingHomeContentTypes());
    dispatch(startLoadingHomeContentItems());
  }, [dispatch]);

  useEffect(() => {
    if (!typeKey && typesCatalog.length) {
      setTypeKey(typesCatalog[0].type_key);
    }
  }, [typesCatalog, typeKey]);

  const currentType = useMemo(
    () => (typesCatalog || []).find((row) => row.type_key === typeKey) || null,
    [typesCatalog, typeKey]
  );

  const fields = currentType?.field_schema?.fields || [];

  const visibleItems = useMemo(
    () =>
      (items || [])
        .filter((row) => (!typeKey || row.type_key === typeKey) && (!lang || row.lang === lang))
        .sort((a, b) => (a.sort || 0) - (b.sort || 0)),
    [items, typeKey, lang]
  );

  const startCreate = () => {
    setEditingId('new');
    setDraft(emptyDraft(typeKey, lang));
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      type_key: row.type_key,
      lang: row.lang,
      sort: row.sort,
      title: row.title || '',
      text: row.text || '',
      image_url: row.image_url || '',
      reference: row.reference || '',
      module_key: row.module_key || '',
      enabled: row.enabled !== false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft(typeKey, lang));
  };

  const onSave = async () => {
    const payload = {
      ...draft,
      type_key: typeKey,
      lang,
      sort: parseInt(draft.sort, 10) || 1,
      enabled: !!draft.enabled,
    };
    if (editingId === 'new') {
      const body = await dispatch(startAddHomeContentItem(payload));
      if (body && body.ok) cancelEdit();
    } else {
      const body = await dispatch(startUpdateHomeContentItem(editingId, payload));
      if (body && body.ok) cancelEdit();
    }
  };

  const onDelete = async (row) => {
    const result = await Swal.fire({
      title: t('homeContent.confirmDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('buttons.delete', { defaultValue: 'Eliminar' }),
      cancelButtonText: t('buttons.cancel', { defaultValue: 'Cancelar' }),
    });
    if (result.isConfirmed) {
      dispatch(startDeleteHomeContentItem(row.id));
    }
  };

  const typeLabel = (row) => t(row.i18n_key || `homeContent.types.${row.type_key}`, { defaultValue: row.type_key });

  const fieldLabel = (key) => t(`homeContent.fields.${key}`, { defaultValue: key });

  return (
    <div className="container mt-4 mb-5">
      <h3>{t('homeContent.title')}</h3>
      <p className="text-muted">{t('homeContent.subtitle')}</p>
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
        <div>
          <label className="form-label mb-1">{t('homeContent.fields.type')}</label>
          <select className="form-select" value={typeKey} onChange={(e) => { setTypeKey(e.target.value); cancelEdit(); }}>
            {(typesCatalog || []).map((row) => (
              <option key={row.type_key} value={row.type_key}>{typeLabel(row)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label mb-1">{t('homeContent.fields.lang')}</label>
          <select className="form-select" value={lang} onChange={(e) => { setLang(e.target.value); cancelEdit(); }}>
            <option value="es">es</option>
            <option value="en">en</option>
          </select>
        </div>
        <button type="button" className="btn btn-dark" onClick={startCreate} disabled={!typeKey}>
          {t('homeContent.add')}
        </button>
      </div>
      {currentType ? (
        <p className="small text-muted">
          {t('homeContent.componentHint', { component: currentType.react_component, surface: currentType.surface })}
        </p>
      ) : null}

      {editingId ? (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">{editingId === 'new' ? t('homeContent.add') : t('homeContent.edit')}</h5>
            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label">{t('homeContent.fields.sort')}</label>
                <input className="form-control" type="number" value={draft.sort} onChange={(e) => setDraft({ ...draft, sort: e.target.value })} />
              </div>
              {fields.map((field) => (
                <div key={field.key} className={field.kind === 'textarea' ? 'col-12' : 'col-md-6'}>
                  <label className="form-label">{fieldLabel(field.key)}{field.required ? ' *' : ''}</label>
                  {field.kind === 'textarea' ? (
                    <textarea className="form-control" rows={4} value={draft[field.key] || ''} onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })} />
                  ) : (
                    <input className="form-control" value={draft[field.key] || ''} onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })} />
                  )}
                </div>
              ))}
              <div className="col-12">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="home-content-enabled" checked={!!draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                  <label className="form-check-label" htmlFor="home-content-enabled">{t('homeContent.fields.enabled')}</label>
                </div>
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button type="button" className="btn btn-dark" onClick={onSave}>{t('buttons.save', { defaultValue: 'Guardar' })}</button>
              <button type="button" className="btn btn-outline-secondary" onClick={cancelEdit}>{t('buttons.cancel', { defaultValue: 'Cancelar' })}</button>
            </div>
          </div>
        </div>
      ) : null}

      {loading ? <p>{t('homeContent.loading')}</p> : null}
      <div className="row g-3">
        {visibleItems.map((row) => (
          <div className="col-md-6" key={row.id}>
            <div className="card h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <strong>{row.title || t('homeContent.untitled')}</strong>
                  <span className="badge bg-secondary">{row.sort}</span>
                </div>
                {row.module_key ? <div className="small text-muted">{row.module_key}</div> : null}
                {row.reference ? <div className="small">{row.reference}</div> : null}
                {row.image_url ? <div className="small text-truncate">{row.image_url}</div> : null}
                <p className="small mt-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{(row.text || '').slice(0, 180)}{(row.text || '').length > 180 ? '…' : ''}</p>
              </div>
              <div className="card-footer bg-white d-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => startEdit(row)}>{t('buttons.edit', { defaultValue: 'Editar' })}</button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onDelete(row)}>{t('buttons.delete', { defaultValue: 'Eliminar' })}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!loading && visibleItems.length === 0 ? (
        <p className="text-muted mt-3">{t('homeContent.empty')}</p>
      ) : null}
    </div>
  );
};

export default AdminHomeContentScreen;
